// kiosk — the self-order panel at a counter (pages/customer/KioskOrder.jsx).
// See README.md. Keys must exist in BOTH maps (checked by npm run check:locales).
//
// Read standing up, at arm's length, by somebody who has never seen the
// product before and will not read a second sentence. Every string here is
// shorter and plainer than its equivalent on the guest menu, and the French
// is written for Mauritius: polite vous, proper accents, and short enough to
// fit the same button as the English at 34px.
export const en = {
  'kiosk.loading': 'Bringing up the menu…',
  'kiosk.error.title': 'This screen is not ready',
  'kiosk.error.notSetUp': 'This kiosk has not been pointed at a branch yet. A member of staff can set it up.',
  'kiosk.error.noMenu': 'No menu is available at this counter right now.',
  'kiosk.error.offline': 'The kitchen cannot be reached. Please order at the counter.',
  // The server refused the kiosk menu or a kiosk order because no registered
  // machine stamped the request (code KIOSK_NOT_REGISTERED).
  'kiosk.error.notRegistered': 'This kiosk is not registered. A member of staff can pair it under Settings → Devices. Please order at the counter.',

  // --- Attract ---------------------------------------------------------------
  // On the promo poster slide, where the poster does the talking.
  'kiosk.attract.ctaNow': 'Touch to order now',

  // --- Order type ------------------------------------------------------------
  'kiosk.mode.title': 'How are you eating today?',
  'kiosk.mode.eatIn': 'Dine in',
  'kiosk.mode.takeAway': 'Take away',
  'kiosk.mode.change': 'change',
  'kiosk.mode.keep': 'Back to the menu',

  // --- Header ----------------------------------------------------------------
  'kiosk.header.hours': 'Open {open} – {close}',
  'kiosk.a11y.toggle': 'Lower the screen',
  'kiosk.a11y.on': 'Everything you can press is now in the lower half of the screen.',
  'kiosk.a11y.alt': 'A friendly monster pointing down at the lower half of the screen',

  // --- Menu ------------------------------------------------------------------
  'kiosk.menu.all': 'All',
  'kiosk.menu.empty': 'Nothing is being served from this counter yet.',
  'kiosk.item.add': 'Add',
  'kiosk.item.free': 'Free',
  'kiosk.item.onRequest': 'On request',
  'kiosk.item.fromPrice': 'from',
  'kiosk.item.soldOut': 'Sold out',
  'kiosk.item.contains': 'Contains {list}',
  'kiosk.variant.regular': 'Regular',

  // --- Item sheet ------------------------------------------------------------
  'kiosk.sheet.choose': 'Choose variant',
  'kiosk.sheet.extras': 'Add something to it',
  'kiosk.sheet.howMany': 'How many?',
  'kiosk.sheet.note': 'Anything the kitchen should know?',
  'kiosk.sheet.notePlaceholder': 'No onion, extra spicy…',
  'kiosk.sheet.pairs': 'Goes well with',
  'kiosk.sheet.cancel': 'Cancel',
  'kiosk.sheet.add': 'Add · {total}',
  'kiosk.sheet.less': 'One less',
  'kiosk.sheet.more': 'One more',

  // --- Goes well with: the panel after a dish is added -----------------------
  'kiosk.suggest.title': 'Added to your order',
  'kiosk.suggest.sub': '{name} is in. Anything to go with it?',
  'kiosk.suggest.skip': 'No thanks',

  // --- Cart ------------------------------------------------------------------
  'kiosk.cart.items': '{count} item(s)',
  'kiosk.cart.view': 'View order',
  'kiosk.cart.title': 'Your order',
  'kiosk.cart.empty': 'Nothing chosen yet.',
  'kiosk.cart.total': 'Total',
  'kiosk.cart.more': 'Add more',
  'kiosk.cart.pay': 'Pay now',

  // --- Which one: a tile's − or + when the dish is in the cart several ways --
  'kiosk.which.title': 'Which one?',
  'kiosk.which.sub': '{name} is already in your order, as listed below. Change the line you mean, or add it with a different option.',
  'kiosk.which.again': 'Same again',
  'kiosk.which.options': 'Or pick another',
  'kiosk.which.another': 'Extras or a note',
  'kiosk.which.done': 'Done',

  // --- Payment ---------------------------------------------------------------
  // --- Who the order is for -------------------------------------------------
  'kiosk.who.title': 'Your number, please',
  'kiosk.who.sub': 'It goes on your receipt and collects your points. Your name is optional.',
  'kiosk.who.phone': 'Phone number',
  'kiosk.who.show': 'Show my number',
  'kiosk.who.hide': 'Hide my number',
  'kiosk.who.hiddenNote': 'Part of your number is hidden so nobody behind you can read it.',
  'kiosk.who.phonePlaceholder': 'e.g. 5xxx xxxx',
  'kiosk.who.name': 'Name (optional)',
  'kiosk.who.namePlaceholder': 'Tap the letters, or skip it',
  'kiosk.who.space': 'Space',
  'kiosk.who.backspace': 'Delete the last character',
  'kiosk.who.continue': 'Continue to payment',
  'kiosk.who.needPhone': 'Enter your number',

  // --- Paying (the guest menu's payment sheet, at kiosk size) --------------
  'kiosk.pay.title': 'Pay for your order',
  'kiosk.pay.demo': 'Demo payment — no money moves.',
  'kiosk.pay.totalLabel': 'Total to pay',
  // Tax, under a price and under the total. {taxes} is the restaurant's own
  // tax names with their rates ("VAT 15%"), never translated.
  'kiosk.tax.incl': 'incl. {taxes}',
  'kiosk.tax.plus': '{net} + {taxes}',
  'kiosk.tax.included': 'Includes {taxes}',
  'kiosk.pay.methodCard': 'Card',
  'kiosk.pay.methodJuice': 'Juice',
  'kiosk.pay.methodMyt': 'my.t money',
  'kiosk.pay.cardNumber': 'Card number (demo)',
  'kiosk.pay.cardExpiry': 'Expiry (demo)',
  'kiosk.pay.cardCvv': 'CVV (demo)',
  'kiosk.pay.payNow': 'Pay {total}',
  'kiosk.pay.receiptTo': 'Your receipt goes to {phone}.',
  'kiosk.pay.placing': 'Sending your order to the kitchen…',
  'kiosk.pay.back': 'Back to my order',
  'kiosk.pay.failed': 'That did not go through. Please try again, or order at the counter.',

  // --- Done ------------------------------------------------------------------
  'kiosk.done.title': 'Thank you — the kitchen has it',
  'kiosk.done.sub': 'Your number is',
  'kiosk.done.number': 'Order {number}',
  'kiosk.done.eatIn': 'Take a seat — we will bring it to you.',
  'kiosk.done.takeAway': 'Wait by the counter — we will call your number.',
  // The number the guest typed rides in, so the promise names where it lands.
  'kiosk.done.notify': 'Your order details are on their way to {phone} by WhatsApp or SMS — and we will message you again the moment your food is ready.',
  'kiosk.done.enjoy': 'Enjoy your meal!',
  'kiosk.done.finish': 'Done',

  // --- The empty ad slot on the attract screen ------------------------------
  'kiosk.adslot.title': 'Show your ad here',
  'kiosk.adslot.sub': 'This screen is waiting for something good.',
  'kiosk.adslot.alt': 'A friendly monster holding an empty advertising space',

  // --- Kiosk Settings (staff screen: Settings -> Kiosk Settings) ------------
  'kiosk.settings.title': 'Kiosk Settings',
  'kiosk.settings.intro': 'Every self-order kiosk you have, filed under its restaurant group and branch, what each branch plays on screen while nobody is standing at it, and how long a kiosk waits before clearing an order somebody walked away from.',
  'kiosk.settings.loading': 'Loading…',
  'kiosk.settings.loadFailed': 'Could not load your kiosks.',
  'kiosk.settings.count': '{active} of {total} kiosks are active.',
  'kiosk.settings.noneYet': 'No machine is registered on this restaurant at all.',
  'kiosk.settings.noneButOthers': 'No kiosk is registered — though {kinds} are. A machine registers as a kiosk only when the kiosk app is paired with a code cut for a kiosk; a till or a kitchen display is a different kind and lives under Settings → Devices.',
  'kiosk.settings.pairingNote': 'A machine is registered by PAIRING it: generate a code under Settings → Devices and enter it on the kiosk\u2019s setup screen. Signing in does not register anything \u2014 the kiosk app has no login at all.',
  'kiosk.settings.kind.pos': 'POS terminal(s)',
  'kiosk.settings.kind.android': 'Android device(s)',
  'kiosk.settings.kind.kds': 'kitchen display(s)',
  'kiosk.settings.kind.other': 'other machine(s)',
  'kiosk.settings.noGroup': 'No restaurant group',
  'kiosk.settings.noBranchGroup': 'Not assigned to a branch',
  'kiosk.settings.noBranch': 'No branch',
  'kiosk.settings.branchInactive': '(branch inactive)',
  'kiosk.settings.branchCount': '{active} of {total} active',
  'kiosk.settings.branchEmpty': 'No kiosk at this branch yet.',
  'kiosk.settings.online': 'Connected now',
  'kiosk.settings.offline': 'Not connected',
  'kiosk.settings.status.active': 'Active',
  'kiosk.settings.status.pending': 'Awaiting approval',
  'kiosk.settings.status.revoked': 'De-registered',
  'kiosk.settings.status.denied': 'Denied',
  'kiosk.settings.ads.title': 'What this branch plays when idle',
  'kiosk.settings.ads.hint': 'Posters and clips, in turn, on every kiosk at this branch. About {seconds} seconds is right for a clip. Pictures up to 5 MB, clips up to 25 MB.',
  'kiosk.settings.ads.add': 'Add a poster or clip',
  'kiosk.settings.ads.empty': 'Nothing added — these kiosks show their own built-in slides.',
  'kiosk.settings.ads.poster': 'Poster',
  'kiosk.settings.ads.clip': 'Clip',
  'kiosk.settings.ads.playing': 'Playing',
  'kiosk.settings.ads.paused': 'Paused',
  'kiosk.settings.ads.play': 'Play it',
  'kiosk.settings.ads.pause': 'Pause it',
  'kiosk.settings.ads.remove': 'Remove',
  'kiosk.settings.ads.removeTitle': 'Remove this from the kiosks?',
  'kiosk.settings.ads.removeBody': '{name} stops playing and the file is deleted. Pause it instead to keep it for later.',
  'kiosk.settings.ads.added': 'Added — the kiosks pick it up on their next idle loop.',
  'kiosk.settings.ads.loadFailed': 'Could not load what this branch is playing.',
  'kiosk.settings.ads.uploadFailed': 'That file could not be added.',
  'kiosk.settings.ads.saveFailed': 'That change could not be saved.',
  'kiosk.settings.idle.title': 'How long a kiosk waits',
  'kiosk.settings.idle.hint': 'A guest who walks away mid-order must not become the next guest’s bill. After this long without a touch the kiosk asks “Still there?”, and when the countdown runs out it clears the order and returns to its attract screen. Any touch starts the clock again.',
  'kiosk.settings.idle.wait': 'Ask “Still there?” after',
  'kiosk.settings.idle.countdown': 'Then clear the order after',
  'kiosk.settings.idle.seconds': 'seconds',
  'kiosk.settings.idle.save': 'Save',
  'kiosk.settings.idle.saved': 'Saved — the kiosks use it from their next order.',
  'kiosk.settings.idle.invalid': 'Keep the wait between {minIdle} and {maxIdle} seconds, and the countdown between {minCount} and {maxCount}.',
  'kiosk.settings.idle.loadFailed': 'Could not load how long this branch’s kiosks wait.',
  'kiosk.settings.idle.saveFailed': 'That change could not be saved.',

  // --- Idle ------------------------------------------------------------------
  'kiosk.idle.title': 'Still there?',
  'kiosk.idle.body': 'Your order will be cleared so the next guest can start fresh.',
  'kiosk.idle.stay': 'I am still ordering',
  'kiosk.idle.finish': 'Clear my order',

  // --- Cancel (top bar) ------------------------------------------------------
  'kiosk.cancel.button': 'Cancel',
  'kiosk.cancel.title': 'Cancel this order?',
  'kiosk.cancel.body': 'Everything in your order is cleared and the screen goes back to the start.',
  'kiosk.cancel.keep': 'Keep ordering',
  'kiosk.cancel.confirm': 'Yes, cancel my order',
};

export const fr = {
  'kiosk.loading': 'Chargement de la carte…',
  'kiosk.error.title': 'Cet écran n’est pas prêt',
  'kiosk.error.notSetUp': 'Cette borne n’est pas encore reliée à un établissement. Un membre du personnel peut la configurer.',
  'kiosk.error.noMenu': 'Aucune carte n’est disponible à ce comptoir pour le moment.',
  'kiosk.error.offline': 'La cuisine est injoignable. Veuillez commander au comptoir.',
  'kiosk.error.notRegistered': 'Cette borne n’est pas enregistrée. Un membre du personnel peut l’appairer dans Paramètres → Appareils. Veuillez commander au comptoir.',

  'kiosk.attract.ctaNow': 'Touchez pour commander maintenant',

  'kiosk.mode.title': 'Comment mangez-vous aujourd’hui ?',
  'kiosk.mode.eatIn': 'Sur place',
  'kiosk.mode.takeAway': 'À emporter',
  'kiosk.mode.change': 'modifier',
  'kiosk.mode.keep': 'Retour à la carte',

  'kiosk.header.hours': 'Ouvert {open} – {close}',
  'kiosk.a11y.toggle': 'Abaisser l’écran',
  'kiosk.a11y.on': 'Tout ce que vous pouvez toucher se trouve maintenant dans la moitié basse de l’écran.',
  'kiosk.a11y.alt': 'Un petit monstre sympathique qui montre la moitié basse de l’écran',

  'kiosk.menu.all': 'Tout',
  'kiosk.menu.empty': 'Rien n’est encore servi à ce comptoir.',
  'kiosk.item.add': 'Ajouter',
  'kiosk.item.free': 'Offert',
  'kiosk.item.onRequest': 'Sur demande',
  'kiosk.item.fromPrice': 'à partir de',
  'kiosk.item.soldOut': 'Épuisé',
  'kiosk.item.contains': 'Contient {list}',
  'kiosk.variant.regular': 'Normal',

  'kiosk.sheet.choose': 'Choisissez une variante',
  'kiosk.sheet.extras': 'Ajoutez un supplément',
  'kiosk.sheet.howMany': 'Combien ?',
  'kiosk.sheet.note': 'Quelque chose à signaler à la cuisine ?',
  'kiosk.sheet.notePlaceholder': 'Sans oignon, plus épicé…',
  'kiosk.sheet.pairs': 'Se marie bien avec',
  'kiosk.sheet.cancel': 'Annuler',
  'kiosk.sheet.add': 'Ajouter · {total}',
  'kiosk.sheet.less': 'Un de moins',
  'kiosk.sheet.more': 'Un de plus',

  // --- Se marie bien avec : le panneau après l’ajout d’un plat ---------------
  'kiosk.suggest.title': 'Ajouté à votre commande',
  'kiosk.suggest.sub': '{name} est ajouté. Quelque chose pour l’accompagner ?',
  'kiosk.suggest.skip': 'Non merci',

  'kiosk.cart.items': '{count} article(s)',
  'kiosk.cart.view': 'Voir ma commande',
  'kiosk.cart.title': 'Votre commande',
  'kiosk.cart.empty': 'Rien de choisi pour l’instant.',
  'kiosk.cart.total': 'Total',
  'kiosk.cart.more': 'Ajouter encore',
  'kiosk.cart.pay': 'Payer maintenant',

  'kiosk.which.title': 'Quelle version ?',
  'kiosk.which.sub': '{name} est déjà dans votre commande, voir ci-dessous. Modifiez la ligne voulue, ou ajoutez une autre version.',
  'kiosk.which.again': 'La même chose',
  'kiosk.which.options': 'Ou choisissez une autre',
  'kiosk.which.another': 'Suppléments ou note',
  'kiosk.which.done': 'Terminé',

  // --- Who the order is for -------------------------------------------------
  'kiosk.who.title': 'Votre numéro, s’il vous plaît',
  'kiosk.who.sub': 'Il figure sur votre reçu et cumule vos points. Votre nom est facultatif.',
  'kiosk.who.phone': 'Numéro de téléphone',
  'kiosk.who.show': 'Afficher mon numéro',
  'kiosk.who.hide': 'Masquer mon numéro',
  'kiosk.who.hiddenNote': 'Une partie de votre numéro est masquée pour que personne derrière vous ne puisse le lire.',
  'kiosk.who.phonePlaceholder': 'ex. 5xxx xxxx',
  'kiosk.who.name': 'Nom (facultatif)',
  'kiosk.who.namePlaceholder': 'Tapez les lettres, ou passez',
  'kiosk.who.space': 'Espace',
  'kiosk.who.backspace': 'Effacer le dernier caractère',
  'kiosk.who.continue': 'Continuer vers le paiement',
  'kiosk.who.needPhone': 'Saisissez votre numéro',

  // --- Paying (the guest menu's payment sheet, at kiosk size) --------------
  'kiosk.pay.demo': 'Paiement de démonstration — aucun argent ne circule.',
  'kiosk.pay.totalLabel': 'Total à payer',
  'kiosk.tax.incl': 'dont {taxes}',
  'kiosk.tax.plus': '{net} + {taxes}',
  'kiosk.tax.included': 'Dont {taxes}',
  'kiosk.pay.methodCard': 'Carte',
  'kiosk.pay.methodJuice': 'Juice',
  'kiosk.pay.methodMyt': 'my.t money',
  'kiosk.pay.cardNumber': 'Numéro de carte (démo)',
  'kiosk.pay.cardExpiry': 'Expiration (démo)',
  'kiosk.pay.cardCvv': 'CVV (démo)',
  'kiosk.pay.payNow': 'Payer {total}',
  'kiosk.pay.receiptTo': 'Votre reçu sera envoyé au {phone}.',
  'kiosk.pay.title': 'Payer votre commande',
  'kiosk.pay.placing': 'Envoi de votre commande à la cuisine…',
  'kiosk.pay.back': 'Retour à ma commande',
  'kiosk.pay.failed': 'Le paiement n’a pas abouti. Réessayez, ou commandez au comptoir.',

  'kiosk.done.title': 'Merci — la cuisine l’a reçue',
  'kiosk.done.sub': 'Votre numéro est',
  'kiosk.done.number': 'Commande {number}',
  'kiosk.done.eatIn': 'Installez-vous — nous vous l’apportons.',
  'kiosk.done.takeAway': 'Patientez près du comptoir — nous appellerons votre numéro.',
  'kiosk.done.notify': 'Les détails de votre commande vous seront envoyés au {phone} par WhatsApp ou SMS — et nous vous préviendrons dès que votre repas sera prêt.',
  'kiosk.done.enjoy': 'Bon appétit !',
  'kiosk.done.finish': 'Terminé',

  'kiosk.idle.title': 'Toujours là ?',
  'kiosk.idle.body': 'Votre commande sera effacée pour que le prochain client reparte de zéro.',
  'kiosk.idle.stay': 'Je commande encore',
  'kiosk.idle.finish': 'Effacer ma commande',

  // --- Cancel (top bar) ------------------------------------------------------
  'kiosk.cancel.button': 'Annuler',
  'kiosk.cancel.title': 'Annuler cette commande ?',
  'kiosk.cancel.body': 'Tout le contenu de votre commande est effacé et l’écran revient au début.',
  'kiosk.cancel.keep': 'Continuer ma commande',
  'kiosk.cancel.confirm': 'Oui, annuler ma commande',

  // --- The empty ad slot on the attract screen ------------------------------
  'kiosk.adslot.title': 'Votre publicité ici',
  'kiosk.adslot.sub': 'Cet écran attend quelque chose de bien.',
  'kiosk.adslot.alt': 'Un petit monstre sympathique tenant un espace publicitaire vide',

  // --- Kiosk Settings (staff screen: Settings -> Kiosk Settings) ------------
  'kiosk.settings.title': 'Paramètres des bornes',
  'kiosk.settings.intro': 'Toutes vos bornes de commande, classées par groupe de restaurants et par succursale, ce que chaque succursale diffuse à l’écran lorsque personne ne s’y trouve, et le délai avant qu’une borne n’efface une commande abandonnée.',
  'kiosk.settings.loading': 'Chargement…',
  'kiosk.settings.loadFailed': 'Impossible de charger vos bornes.',
  'kiosk.settings.count': '{active} borne(s) active(s) sur {total}.',
  'kiosk.settings.noneYet': 'Aucune machine n’est enregistrée pour ce restaurant.',
  'kiosk.settings.noneButOthers': 'Aucune borne n’est enregistrée — mais {kinds} le sont. Une machine n’est enregistrée comme borne que si l’application borne est appairée avec un code destiné à une borne ; une caisse ou un écran de cuisine est d’un autre type et figure dans Paramètres → Appareils.',
  'kiosk.settings.pairingNote': 'Une machine s’enregistre par APPAIRAGE : générez un code dans Paramètres → Appareils et saisissez-le sur l’écran de configuration de la borne. Se connecter n’enregistre rien — l’application borne n’a aucune connexion.',
  'kiosk.settings.kind.pos': 'caisse(s)',
  'kiosk.settings.kind.android': 'appareil(s) Android',
  'kiosk.settings.kind.kds': 'écran(s) de cuisine',
  'kiosk.settings.kind.other': 'autre(s) machine(s)',
  'kiosk.settings.noGroup': 'Aucun groupe de restaurants',
  'kiosk.settings.noBranchGroup': 'Non rattachée à une succursale',
  'kiosk.settings.noBranch': 'Aucune succursale',
  'kiosk.settings.branchInactive': '(succursale inactive)',
  'kiosk.settings.branchCount': '{active} active(s) sur {total}',
  'kiosk.settings.branchEmpty': 'Aucune borne dans cette succursale pour l’instant.',
  'kiosk.settings.online': 'Connectée maintenant',
  'kiosk.settings.offline': 'Non connectée',
  'kiosk.settings.status.active': 'Active',
  'kiosk.settings.status.pending': 'En attente d’approbation',
  'kiosk.settings.status.revoked': 'Désenregistrée',
  'kiosk.settings.status.denied': 'Refusée',
  'kiosk.settings.ads.title': 'Ce que diffuse cette succursale au repos',
  'kiosk.settings.ads.hint': 'Affiches et clips, à tour de rôle, sur toutes les bornes de cette succursale. Environ {seconds} secondes conviennent pour un clip. Images jusqu’à 5 Mo, clips jusqu’à 25 Mo.',
  'kiosk.settings.ads.add': 'Ajouter une affiche ou un clip',
  'kiosk.settings.ads.empty': 'Rien d’ajouté — ces bornes affichent leurs propres visuels.',
  'kiosk.settings.ads.poster': 'Affiche',
  'kiosk.settings.ads.clip': 'Clip',
  'kiosk.settings.ads.playing': 'En diffusion',
  'kiosk.settings.ads.paused': 'En pause',
  'kiosk.settings.ads.play': 'Diffuser',
  'kiosk.settings.ads.pause': 'Mettre en pause',
  'kiosk.settings.ads.remove': 'Supprimer',
  'kiosk.settings.ads.removeTitle': 'Retirer ceci des bornes ?',
  'kiosk.settings.ads.removeBody': '{name} cesse d’être diffusé et le fichier est supprimé. Mettez-le plutôt en pause pour le conserver.',
  'kiosk.settings.ads.added': 'Ajouté — les bornes le reprennent à leur prochaine boucle.',
  'kiosk.settings.ads.loadFailed': 'Impossible de charger ce que diffuse cette succursale.',
  'kiosk.settings.ads.uploadFailed': 'Ce fichier n’a pas pu être ajouté.',
  'kiosk.settings.ads.saveFailed': 'Cette modification n’a pas pu être enregistrée.',
  'kiosk.settings.idle.title': 'Délai d’attente des bornes',
  'kiosk.settings.idle.hint': 'Un client qui s’éloigne en pleine commande ne doit pas devenir l’addition du suivant. Passé ce délai sans toucher l’écran, la borne demande « Toujours là ? », puis, à la fin du compte à rebours, efface la commande et revient à son écran d’accueil. Tout contact relance le délai.',
  'kiosk.settings.idle.wait': 'Demander « Toujours là ? » après',
  'kiosk.settings.idle.countdown': 'Puis effacer la commande après',
  'kiosk.settings.idle.seconds': 'secondes',
  'kiosk.settings.idle.save': 'Enregistrer',
  'kiosk.settings.idle.saved': 'Enregistré — les bornes l’appliquent dès la prochaine commande.',
  'kiosk.settings.idle.invalid': 'Gardez l’attente entre {minIdle} et {maxIdle} secondes, et le compte à rebours entre {minCount} et {maxCount}.',
  'kiosk.settings.idle.loadFailed': 'Impossible de charger le délai d’attente des bornes de cette succursale.',
  'kiosk.settings.idle.saveFailed': 'Cette modification n’a pas pu être enregistrée.',
};
